/**
 * assistantSuggestions — page-aware "prompt starters" for the AI marketer
 * (FIX-AI-6 follow-up). Each suggestion maps to a tool the assistant actually
 * has and is self-contained / fail-soft (no user-supplied id), so it returns a
 * useful answer instead of an error. Every prompt below was verified end-to-end
 * against the REAL assistant (anthropic) with the same page-context prefix the
 * widget sends — see the per-page tool in the comment.
 *
 * Clicking a suggestion only fills the input (never touches the conversation),
 * so an existing chat history is preserved.
 */
import { resolveNavPath } from "./useNavFavorites";
import { ALL_NAV_ITEMS } from "./navModel";

export interface AssistantSuggestion {
  /** Full prompt inserted into the input on click. */
  prompt: string;
  /** Short label for the compact chip strip. */
  label: string;
}

const SUGGESTIONS: Record<string, AssistantSuggestion[]> = {
  // Сводка — list_recent_broadcasts, compare_periods
  "/": [
    { prompt: "Какие 5 рассылок ушли последними?", label: "Последние рассылки" },
    { prompt: "Сравни сколько доставлено за эту и прошлую неделю", label: "Сравнить недели" },
  ],
  // Рассылки — list_recent_broadcasts + get_campaign_stats, compare_periods, draft_broadcast
  "/broadcasts": [
    { prompt: "Покажи статистику последней рассылки", label: "Статистика" },
    { prompt: "Сравни открытия за эту и прошлую неделю", label: "Сравнить открытия" },
    { prompt: "Собери рассылку для VIP-клиентов про скидку 10%", label: "Рассылка для VIP" },
  ],
  // Сегменты — draft_segment
  "/segments": [
    { prompt: "Построй сегмент: клиенты без покупок за 30 дней", label: "Без покупок 30 дней" },
    { prompt: "Построй сегмент: открыли письмо за последние 7 дней", label: "Открыли письмо" },
  ],
  // Сценарии — create_journey
  "/scenarios": [
    { prompt: "Собери сценарий: через час после брошенной корзины напомни письмом", label: "Сценарий корзины" },
  ],
  // Опросы — publish_survey
  "/surveys": [
    { prompt: "Собери опрос NPS про доставку", label: "Опрос про доставку" },
  ],
  // Веб-инструменты — create_experience
  "/experiences": [
    { prompt: "Собери попап со скидкой 10% при выходе со страницы", label: "Попап со скидкой" },
  ],
  // Аналитика — compare_periods (метрики: delivered/opened/clicked/cost — без «выручки»)
  "/analytics": [
    { prompt: "Сравни клики за этот и прошлый месяц", label: "Сравнить клики" },
    { prompt: "Сравни сколько доставлено за эту и прошлую неделю", label: "Сравнить доставку" },
  ],
  // Доставляемость — compare_periods (delivered / failed)
  "/analytics/deliverability": [
    { prompt: "Сравни доставку за эту и прошлую неделю", label: "Сравнить доставку" },
    { prompt: "Сравни сколько писем не дошло за эту и прошлую неделю", label: "Сравнить недоставку" },
  ],
  // Промокоды — нет промо-инструмента: собираем кампанию С промокодом (draft_broadcast / create_experience)
  "/promo-codes": [
    { prompt: "Собери рассылку для активных клиентов с промокодом на скидку 10%", label: "Рассылка с промокодом" },
    { prompt: "Собери попап с промокодом на скидку 10%", label: "Попап с промокодом" },
  ],
  // Лояльность — draft_segment / draft_broadcast
  "/loyalty/tiers": [
    { prompt: "Построй сегмент: клиенты с высоким баллом лояльности", label: "Сегмент лояльных" },
    { prompt: "Собери рассылку для самых лояльных клиентов про закрытую распродажу", label: "Рассылка лояльным" },
  ],
};
// Sub-pages reuse a parent set.
SUGGESTIONS["/analytics/revenue"] = SUGGESTIONS["/analytics"];

/** Fallback for pages without a curated set — all verified-safe, id-free. */
const GENERIC: AssistantSuggestion[] = [
  { prompt: "Покажи статистику последней рассылки", label: "Статистика" },
  { prompt: "Построй сегмент: клиенты без покупок за 30 дней", label: "Построить сегмент" },
  { prompt: "Сравни сколько доставлено за эту и прошлую неделю", label: "Сравнить недели" },
];

/** Curated suggestions for the operator's current route (page-aware). */
export function suggestionsForRoute(routePath: string | undefined | null): AssistantSuggestion[] {
  const navPath = typeof routePath === "string" ? resolveNavPath(routePath) : null;
  return (navPath && SUGGESTIONS[navPath]) || GENERIC;
}

/** Friendly page label for the context pill ("" when not a known nav page). */
export function pageLabelForRoute(routePath: string | undefined | null, t: (k: string) => string): string {
  const navPath = typeof routePath === "string" ? resolveNavPath(routePath) : null;
  if (!navPath) return "";
  const item = ALL_NAV_ITEMS.find((i) => i.path === navPath);
  return item ? t(item.labelKey) : "";
}
